import * as THREE from "three";
import type {
  CollisionWorld,
  EntityKind,
  LimbId,
  ScavRole,
  Vec3,
  WildlifeSpecies,
} from "@rietz/shared";
import type { InterpPose, PoseGear } from "../net/interpolation.js";
import { createAnimal } from "../render/animals.js";
import { createCharacter, type CharacterRig } from "../render/character.js";
import {
  GEAR_DARK,
  GEAR_MID,
  playerTintFor,
  roleGearFor,
  roleTagInkFor,
  roleTintFor,
} from "../render/palette.js";

/**
 * Remote avatars — other players, scavs and animals — as low-poly rigs sized
 * to the same capsule as the hitbox. Rigs are created on join and destroyed on
 * leave/timeout, keyed by entity id; per-frame work is writing transforms.
 * `kind` and `species` are read ONCE at creation (an entity does not change
 * species). `role` is read on the same terms with one exception: a role is
 * STICKY on the wire (sent once, then omitted) and the interpolator lags ~100
 * ms, so a role that turns up late re-tones the existing rig (`retone`) rather
 * than rebuilding it. An animal skips the whole per-frame identity pass — a
 * deer has no name, gear, reload, lean or device.
 */

interface Avatar {
  rig: CharacterRig;
  /** Marked each frame the entity was seen; unmarked avatars are removed. */
  seen: boolean;
  /**
   * Man, scav or animal. Read once, at creation — a late role is refused for a
   * non-scav and an animal skips the identity pass.
   */
  kind: EntityKind;
  /**
   * What KIND of scav, or `undefined` for a Wilderer and every human. Allowed
   * to arrive once after creation — see `retone`.
   */
  role: ScavRole | undefined;
  /** Last name applied, so the canvas tag is only rebuilt when it changes. */
  name: string;
  /**
   * Last squad flag applied, cached because the ink is baked into the tag's
   * canvas — a re-read every frame would be a texture upload every frame.
   */
  team: boolean;
  /**
   * Last role ink applied, cached for the same reason; derived from `role`, so
   * it moves at most once.
   */
  ink: string | undefined;
  /**
   * Last gear applied, compared FIELD BY FIELD: a fresh snapshot allocates a
   * new object saying the same thing, and rebuilding a weapon model 15 times a
   * second for that would be absurd.
   */
  gear: PoseGear | null;
  /** When the shout over his head expires, or 0 for none, and the line itself.
   *  Kept here rather than read back off the rig, which draws nothing headless. */
  barkUntilMs: number;
  bark: string | null;
}

/** How long one shout stays over a scav's head. Long enough to read a short
 *  line at a glance, short enough that two do not stack. */
export const BARK_SHOW_MS = 2600;

/**
 * `InterpPose` with the role on it. The interpolator carries the role as a
 * TRACK fact beside `name`; this view reads it when present and `undefined`
 * otherwise — the pre-v25 answer.
 */
type RolePose = InterpPose & { role?: ScavRole };

/** This entity's scav role, or `undefined` for a Wilderer and for a human. */
function poseRole(pose: InterpPose): ScavRole | undefined {
  return (pose as RolePose).role;
}

/** `InterpPose` with the species on it (protocol v28) — the same structural
 *  view as `RolePose`, and for the same reason. */
type SpeciesPose = InterpPose & { species?: WildlifeSpecies };

/** Which animal this is, or `undefined` for anything with two legs. */
function poseSpecies(pose: InterpPose): WildlifeSpecies | undefined {
  return (pose as SpeciesPose).species;
}

/**
 * Paints one scav rig in its role's colours, in place — three material colours
 * and a node name, no geometry or scene-graph edits, so it is safe on a rig
 * already standing in the village. The node name carries the role
 * (`scav:boss:<id>`) for scene-graph dumps.
 */
function retone(id: string, rig: CharacterRig, role: ScavRole | undefined): void {
  rig.setTint(roleTintFor(id, role));
  const tones = roleGearFor(role);
  rig.setGearTones(tones.gear, tones.accent);
  rig.group.name = `scav:${role ?? "wilderer"}:${id}`;
}

function sameGear(a: PoseGear | null, b: PoseGear | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.weapon === b.weapon &&
    a.vest === b.vest &&
    a.helmet === b.helmet &&
    a.backpack === b.backpack
  );
}

export class RemoteAvatars {
  private readonly root = new THREE.Group();
  private readonly avatars = new Map<string, Avatar>();
  private readonly camera: THREE.Camera | null;
  private readonly cameraPos = new THREE.Vector3();
  /**
   * The collision world, as a GETTER (replaced on every map change). Only a
   * remote laser needs it, to stop at the wall; OPTIONAL so headless callers
   * work — a beam then draws its full length.
   */
  private readonly world: (() => CollisionWorld) | null;
  /**
   * Ids forced invisible whatever the snapshot says — the killcam sits INSIDE
   * the killer's head, and a head fills the screen.
   */
  private readonly hidden = new Set<string>();

  constructor(
    scene: THREE.Scene,
    camera?: THREE.Camera,
    world?: () => CollisionWorld,
  ) {
    this.root.name = "remotes";
    scene.add(this.root);
    this.camera = camera ?? null;
    this.world = world ?? null;
  }

  get count(): number {
    return this.avatars.size;
  }

  /**
   * Applies one frame's interpolated poses. `dtMs` drives the animation blends
   * only; the pose itself is the interpolator's, so a dropped frame cannot
   * desync it.
   */
  update(poses: readonly InterpPose[], dtMs = 16.7, nowMs = performance.now()): void {
    for (const avatar of this.avatars.values()) avatar.seen = false;

    if (this.camera !== null) {
      this.camera.getWorldPosition(this.cameraPos);
    }

    for (const pose of poses) {
      const role = poseRole(pose);
      const avatar = this.acquire(pose.id, pose.kind, role, poseSpecies(pose));
      avatar.seen = true;

      // An animal is posed and nothing else — every call skipped here is a
      // no-op
      // on an `AnimalRig`; this says what an animal IS.
      if (avatar.kind === "animal") {
        avatar.rig.setPose(
          pose.pos,
          pose.yaw,
          pose.pitch,
          pose.stance,
          pose.anim,
          dtMs,
        );
        avatar.rig.group.visible = !this.hidden.has(pose.id);
        continue;
      }

      // A role that arrived after its rig did. Guarded on the role being NEW,
      // so
      // the common case is one comparison. Never for a player.
      if (role !== undefined && role !== avatar.role && avatar.kind === "scav") {
        avatar.role = role;
        retone(pose.id, avatar.rig, role);
      }

      // One rebuild for any of the three: the tag's ink is part of its texture.
      const ink = roleTagInkFor(avatar.role);
      if (pose.name !== avatar.name || pose.team !== avatar.team || ink !== avatar.ink) {
        avatar.name = pose.name;
        avatar.team = pose.team;
        avatar.ink = ink;
        avatar.rig.setName(pose.name, pose.team, ink);
      }

      if (!sameGear(pose.gear, avatar.gear)) {
        avatar.gear = pose.gear;
        avatar.rig.setGear(pose.gear);
      }

      // Before the pose, so the rig's reload and lean blends advance with THIS
      // frame's `dtMs` rather than a frame behind the flags that set them.
      avatar.rig.setReloading(pose.reload);
      avatar.rig.setLean(pose.lean);
      // The mounted device BEFORE the pose: it can rebuild the weapon model,
      // and a
      // beam on a muzzle about to be replaced would spend a frame on the old
      // gun.
      avatar.rig.setTactical(pose.tac);
      avatar.rig.setPose(
        pose.pos,
        pose.yaw,
        pose.pitch,
        pose.stance,
        pose.anim,
        dtMs,
      );
      // AFTER the pose: the beam is clamped by a ray from the muzzle's WORLD
      // position. A no-op for an unlit laser.
      avatar.rig.updateTactical(this.world === null ? null : this.world());
      // The shout runs on a wall clock, not the pose stream: it goes away on
      // time whatever the packets are doing.
      if (avatar.barkUntilMs !== 0 && nowMs >= avatar.barkUntilMs) {
        avatar.barkUntilMs = 0;
        avatar.bark = null;
        avatar.rig.setBark(null);
      }
      if (this.camera !== null) avatar.rig.updateNameTag(this.cameraPos);
      avatar.rig.group.visible = !this.hidden.has(pose.id);
    }

    for (const [id, avatar] of this.avatars) {
      if (avatar.seen) continue;
      this.destroy(id, avatar);
    }
  }

  /**
   * Puts a line over one entity's head for `BARK_SHOW_MS`. An id with no avatar
   * (never seen, or beyond the interest radius) is dropped silently — the
   * server sends by distance, not by what we happen to be drawing. An animal
   * says nothing.
   */
  bark(id: string, text: string, nowMs: number): void {
    const avatar = this.avatars.get(id);
    if (avatar === undefined || avatar.kind === "animal") return;
    avatar.barkUntilMs = nowMs + BARK_SHOW_MS;
    avatar.bark = text;
    avatar.rig.setBark(text);
  }

  /** Debug/test accessor: the line showing over one entity, or `null`. */
  barkOf(id: string): string | null {
    return this.avatars.get(id)?.bark ?? null;
  }

  /**
   * Where one avatar is standing, or `null` for an id we are not drawing. A
   * fresh object — this is asked per event, not per frame.
   */
  positionOf(id: string): Vec3 | null {
    const avatar = this.avatars.get(id);
    if (avatar === undefined) return null;
    const p = avatar.rig.group.position;
    return { x: p.x, y: p.y, z: p.z };
  }

  /**
   * A hit reaction on one avatar: it jolts away from `from` (the shooter's
   * position, or `null` for unknown). An id with no avatar is dropped — the
   * event is broadcast, the rigs are only the ones in interest range. An
   * animal's rig no-ops.
   */
  hit(targetId: string, limb: LimbId, from: Vec3 | null): void {
    this.avatars.get(targetId)?.rig.hit(limb, from);
  }

  /**
   * Primes one avatar's death fall, on the same terms. The fall itself starts
   * when the interpolated `dead` anim arrives, ~100 ms later.
   */
  kill(id: string, from: Vec3 | null, limb: LimbId | null): void {
    this.avatars.get(id)?.rig.kill(from, limb);
  }

  /** Forces one avatar's rig hidden (or lets it be drawn again). */
  setHidden(id: string, hidden: boolean): void {
    if (hidden) this.hidden.add(id);
    else this.hidden.delete(id);
    const avatar = this.avatars.get(id);
    if (avatar !== undefined) avatar.rig.group.visible = !hidden;
  }

  remove(id: string): void {
    const avatar = this.avatars.get(id);
    if (avatar === undefined) return;
    this.destroy(id, avatar);
  }

  clear(): void {
    for (const [id, avatar] of this.avatars) this.destroy(id, avatar);
    this.hidden.clear();
  }

  // ---------------------------------------------------------------------

  private acquire(
    id: string,
    kind: EntityKind,
    role: ScavRole | undefined,
    species: WildlifeSpecies | undefined,
  ): Avatar {
    const existing = this.avatars.get(id);
    if (existing !== undefined) return existing;

    // A scav is dressed by his ROLE and a PMC by an identity tint over dark
    // grey;
    // no palette overlaps another, so what is walking towards you reads at 60 m
    // (`palette.ts`). The PMC is painted HERE and the scav by `retone`, which
    // has
    // to be re-runnable on a standing rig — one description of a raider.
    const scav = kind === "scav";
    let rig: CharacterRig;
    if (kind === "animal") {
      // An animal dresses itself from its species' coat. The `?? "deer"` is a
      // guess about a case that cannot happen (the server sends `species` on
      // every
      // animal snapshot); the middle-sized animal beats an invisible thing that
      // stops rounds.
      rig = createAnimal(species ?? "deer", id);
    } else {
      rig = createCharacter(scav ? { id } : { id, tint: playerTintFor(id) });
      if (scav) {
        retone(id, rig, role);
      } else {
        rig.setGearTones(GEAR_DARK, GEAR_MID);
        rig.group.name = `remote:${id}`;
      }
    }
    this.root.add(rig.group);

    const avatar: Avatar = {
      rig,
      seen: true,
      kind,
      role: scav ? role : undefined,
      name: "",
      team: false,
      ink: undefined,
      gear: null,
      barkUntilMs: 0,
      bark: null,
    };
    this.avatars.set(id, avatar);
    return avatar;
  }

  private destroy(id: string, avatar: Avatar): void {
    this.root.remove(avatar.rig.group);
    avatar.rig.dispose();
    this.avatars.delete(id);
  }
}
